'use client'

/**
 * Specialty refrigeration panels. The five `.spectab` buttons are the canvas's
 * static markup (generated sections.tsx); this component renders the active
 * panel (sub line, "Shop all" + prev/next, the rail) and wires those buttons
 * by DOM — exactly how the artboard did it — so the header row stays verbatim.
 * Active pill style is the canvas's `spill()` string.
 */
import Link from 'next/link'
import * as React from 'react'
import type { SpecialtyTab } from '@/lib/homepage-2026/types'
import { ProductCard } from './product-card'

const ACTIVE = { background: '#0469f0', borderColor: '#0469f0', color: '#ffffff' }
const LABEL_TO_KEY: Record<string, SpecialtyTab['key']> = { 'Wine & Beverage': 'wine', 'Mini Fridges': 'mini', 'Ice Makers': 'ice', Commercial: 'com', Kegerators: 'keg' }

export function SpecialtyPanels({ tabs }: { tabs: SpecialtyTab[] }) {
  const live = tabs.filter((t) => t.products.length > 0)
  const [key, setKey] = React.useState<SpecialtyTab['key']>(live[0]?.key ?? 'wine')
  const rootRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const section = rootRef.current?.closest('#specialty')
    if (!section) return
    const buttons = Array.from(section.querySelectorAll<HTMLButtonElement>('button.spectab'))
    const handlers = buttons.map((b) => {
      const k = LABEL_TO_KEY[b.textContent?.trim() ?? '']
      const h = () => {
        if (k) setKey(k)
      }
      b.addEventListener('click', h)
      const has = live.some((t) => t.key === k)
      b.style.display = has ? '' : 'none'
      return h
    })
    return () => buttons.forEach((b, i) => b.removeEventListener('click', handlers[i]))
  }, [live])

  React.useEffect(() => {
    const section = rootRef.current?.closest('#specialty')
    if (!section) return
    for (const b of Array.from(section.querySelectorAll<HTMLButtonElement>('button.spectab'))) {
      const k = LABEL_TO_KEY[b.textContent?.trim() ?? '']
      const on = k === key
      b.style.background = on ? ACTIVE.background : ''
      b.style.borderColor = on ? ACTIVE.borderColor : ''
      b.style.color = on ? ACTIVE.color : ''
      b.setAttribute('aria-selected', on ? 'true' : 'false')
    }
  }, [key])

  const active = live.find((t) => t.key === key) ?? live[0]
  if (!active) return null
  return (
    <div ref={rootRef} data-spec={active.key} role="tabpanel" aria-label={active.label}>
      {/* FRIDGE-1096: description first, then the "Shop all" link with the arrows at its right —
          the tab row above is this rail's title row. Same grid as every other rail header. */}
      <div className="@container">
      <div className="railhead railhead-panel" style={{ paddingBottom: '4px' }}>
        <div className="sect-sub railhead-sub" style={{ paddingBottom: '14px' }}>{active.sub}</div>
          <Link className="viewall railhead-link" href={active.href}>
            {active.hrefLabel}
          </Link>
          <span className="railnav">
            <button type="button" aria-label="Previous">
              <svg style={{ transform: 'rotate(180deg)' }}>
                <use href="#i-chev" />
              </svg>
            </button>
            <button type="button" aria-label="Next">
              <svg>
                <use href="#i-chev" />
              </svg>
            </button>
          </span>
      </div>
      </div>
      <div className="rail" key={active.key}>
        {active.products.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </div>
  )
}
